/**
 * THE DESIGN REVIEW, GROUPED THE WAY CANON'S REPORT IS (A3 S9).
 *
 * Canon's design-review report states every finding once: a short version with counts, the items to
 * settle first, then the findings by discipline (design-review prompt v1.5-beta, Chad Garrett,
 * 2026-09-08; the owner's report, 2026-10-26). Brad, 2026-10-27: a client reads each finding's
 * summary, discipline, who acts and status, grouped that way, and never the triage, the detail or a
 * pricing sentence. The API already holds those back (`clientViewOfFinding`); this module only groups
 * what the reader was sent, so a staff reader and a client see the same shape.
 *
 * ⚠️ EACH FINDING ONCE. An open settle-first finding sits in "settle first" and nowhere else; every
 * other finding sits under exactly one discipline. `design-review-report.test.ts` holds the sum.
 */
import type { RecordFactView } from './api.js';
import { isOpenFinding, isRegisterFinding, type RegisterFindingValue } from './register-finding.js';

/** Canon's eight disciplines, in the report's order (design-review prompt, THE REPORT §5). */
export const CANON_DISCIPLINES = [
  'General and code',
  'Civil',
  'Landscape',
  'Architectural',
  'Structural',
  'Mechanical and fire protection',
  'Plumbing',
  'Electrical',
] as const;

/** The group for a finding whose discipline names none of canon's eight. */
export const OTHER_DISCIPLINE = 'Other';

/**
 * Register words that are one of canon's eight under a narrower name. Literal sub-names only: HVAC
 * and fire protection are what "Mechanical and fire protection" covers; code is what "General and
 * code" covers. Nothing here is a judgement about which trade a finding belongs to.
 */
const ALIASES: Readonly<Record<string, string>> = {
  hvac: 'Mechanical and fire protection',
  mechanical: 'Mechanical and fire protection',
  'fire protection': 'Mechanical and fire protection',
  general: 'General and code',
  code: 'General and code',
  'code analysis': 'General and code',
};

const BY_NAME = new Map<string, string>([
  ...CANON_DISCIPLINES.map((d) => [d.toLowerCase(), d] as [string, string]),
  ...Object.entries(ALIASES),
]);

/**
 * The canon discipline a finding sits under: the FIRST discipline its register text names.
 *
 * 🔴 MEASURED on the six non-healthcare registers (371 findings): the discipline is free text, and
 * only 221 use one of canon's eight names exactly. The rest are compounds ("Structural /
 * Architectural") and qualified names ("Architectural (envelope)"). Taking the text before the first
 * "/" or "(" places 317 of 371; the other 54 go under Other. The finding still shows its own text, so
 * nothing the register said is lost by the grouping.
 */
export function disciplineGroup(discipline: string | null | undefined): string {
  const lead = (discipline ?? '').split(/[/(]/)[0]?.trim().toLowerCase() ?? '';
  return BY_NAME.get(lead) ?? OTHER_DISCIPLINE;
}

/** One finding as the report shows it. */
export interface ReportFinding {
  readonly key: string;
  readonly summary: string;
  /** The register's own discipline text, or null when it stated none. */
  readonly discipline: string | null;
  readonly status: string | null;
  readonly whoActs: string | null;
  readonly open: boolean;
  /**
   * Every source document this finding was raised against was withdrawn. Shown, never hidden: a
   * person decides what it means (Brad, 2026-10-02). It still counts where it sits.
   */
  readonly sourceWithdrawn: boolean;
}

export interface DesignReviewReport {
  readonly counts: {
    /** Findings shown: every other count below is a part of this one. */
    readonly shown: number;
    readonly open: number;
    readonly answered: number;
    readonly retired: number;
    /** No status the reader may see: the register left it blank or used a word outside its eight
     * (measured: none of the 371 real findings did). */
    readonly noStatus: number;
    /** Open findings to settle before the work can be priced. */
    readonly settleFirst: number;
    /** Held back from this reader (#1606). `null` = not said. The page does not say why. */
    readonly withheld: number | null;
  };
  readonly settleFirst: readonly ReportFinding[];
  readonly byDiscipline: readonly {
    readonly discipline: string;
    readonly findings: readonly ReportFinding[];
  }[];
}

const ANSWERED = new Set(['ANSWERED', 'CLOSED']);
const RETIRED = new Set(['SUPERSEDED', 'WITHDRAWN']);

/**
 * A staff read carries the triage, and the triage decides; a client's read carries only
 * `settleFirst`, derived from it by the server. A stored `settleFirst` never outranks a triage, so
 * the two views cannot disagree (red-team, S9).
 */
function isSettleFirst(v: RegisterFindingValue): boolean {
  const triage = v.triage?.trim().toUpperCase();
  if (triage) return triage === 'BLOCKS_PRICING';
  return v.settleFirst === true;
}

/** Keys in the register's order: CV-0002 before CV-0010, whatever the server's order was. */
const byKey = (a: ReportFinding, b: ReportFinding) =>
  a.key.localeCompare(b.key, undefined, { numeric: true });

/** Group the design-review findings among `facts` as canon's report does. */
export function designReviewReport(
  facts: readonly RecordFactView[],
  withheld: number | null,
): DesignReviewReport {
  const settle: ReportFinding[] = [];
  const groups = new Map<string, ReportFinding[]>();
  let open = 0;
  let answered = 0;
  let retired = 0;
  let noStatus = 0;

  for (const f of facts) {
    if (f.kind !== 'register_finding' || !isRegisterFinding(f.value)) continue;
    const v = f.value;
    const status = v.status?.trim().toUpperCase() || null;
    const finding: ReportFinding = {
      key: f.key,
      summary: v.summary,
      discipline: v.discipline?.trim() || null,
      status,
      // The owner column, else the party the finding NAMED without a clock (#1602; the S8 check's
      // precon manager) — said as named, so it never reads as an owned, dated item.
      whoActs:
        f.ownerName?.trim() ||
        (v.statedOwner?.trim() ? `${v.statedOwner.trim()} (named, no date set)` : null),
      open: isOpenFinding(v),
      sourceWithdrawn: f.sourceWithdrawal != null,
    };
    if (finding.open) open++;
    else if (status !== null && ANSWERED.has(status)) answered++;
    else if (status !== null && RETIRED.has(status)) retired++;
    else noStatus++;

    // A settled item no longer holds up the price, so it goes back to its discipline.
    if (finding.open && isSettleFirst(v)) {
      settle.push(finding);
      continue;
    }
    const group = disciplineGroup(finding.discipline);
    groups.set(group, [...(groups.get(group) ?? []), finding]);
  }

  const order = [...CANON_DISCIPLINES, OTHER_DISCIPLINE] as readonly string[];
  return {
    counts: {
      shown: open + answered + retired + noStatus,
      open,
      answered,
      retired,
      noStatus,
      settleFirst: settle.length,
      withheld,
    },
    settleFirst: settle.sort(byKey),
    byDiscipline: order
      .filter((d) => groups.has(d))
      .map((d) => ({ discipline: d, findings: (groups.get(d) ?? []).sort(byKey) })),
  };
}
